/* eslint-disable no-use-before-define */
import { createContext } from 'react';

import {
  ArticleItem,
  BannerModuleContainer,
  BaseMetadata,
  CollectionDtoMetadata,
  ContentUnion,
  ExternalCollectionItem,
  GraphicLinkedItem,
  IssueContainer,
  IssueSectionContainer,
  LinkedItem,
  ListContainer,
  MobileArticleItem,
  MobileLinkedItem,
  ModuleContainer,
  ModuleContainerUnion,
  MultiListContainer,
  MultiZoneModuleContainer,
  NewsletterPageContainer,
  NewswiresListContainer,
  OffPlatformListContainer,
  OffPlatformMetadata,
  PageContainer,
  PageModule,
  PageValidation,
  QueryItem,
  SectionAttributes,
  SectionContainer,
  StructureGroupConfig
} from 'data/generated/graphql';
import { AllessehSummariesIncludedAttributes } from 'hooks/useAllessehCollectionSummariesQuery';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import type { DataModelTypeKeys } from './DataModelContext.constants';
import { EntityType } from './DataModelContext.constants';

export type Container =
  | MultiListContainer
  | BannerModuleContainer
  | IssueContainer
  | IssueSectionContainer
  | ListContainer
  | ModuleContainer
  | NewsletterPageContainer
  | OffPlatformListContainer
  | NewswiresListContainer
  | PageContainer
  | SectionContainer
  | MultiZoneModuleContainer
  | GroupContainer;

export type CollectionsType = PageContainer | ModuleContainer | ContentUnion;

export type ContentItem =
  | ArticleItem
  | ExternalCollectionItem
  | QueryItem
  | LinkedItem
  | MobileLinkedItem
  | GraphicLinkedItem
  | MobileArticleItem;

export type EntityComponent = Container | ContentItem;
export type Metadata = BaseMetadata | CollectionDtoMetadata | OffPlatformMetadata;

export type GroupContainer = {
  __typename?: 'GroupContainer';
  collection: Array<EntityComponent>;
  type: 'Group';
  subtype?: string;
  groupId?: string;
  isGroupEnabled?: boolean;
};

type RenderEntityOptions = {
  hierarchyId: string;
  index?: number;
  extraProps?: Record<string, unknown>;
  isHistory?: boolean;
  withoutDragNDrop?: boolean;
  isInEditMode?: boolean;
  isReadOnly?: boolean;
};

export type TraverseEntityOptions = {
  parentHierarchyId?: string;
  entityIndex?: number;
  dontTraverseDisabledGroups?: boolean;
};

type FromAllessehContentOptions = {
  generateUUID?: boolean;
};

export type ArticlePromotionLogData = {
  promotedAt: number;
};

export type ArticlePromotionLog = Record<string, ArticlePromotionLogData> | null;

export interface IDataModelContext<T extends Container> {
  type: DataModelTypeKeys;
  root: T;
  metadata: Metadata;
  hasModelChangedOnce: boolean;
  hasModelChangedRecently: boolean;
  setHasModelChangedRecently: React.Dispatch<React.SetStateAction<boolean>>;
  resetModelChanged: () => void;
  readHierarchyId: {
    (hierarchyId: string): {
      entity: (typeof EntityType)[keyof typeof EntityType];
      index: number;
    }[];
    (hierarchyId: string, onlyIndices: boolean): number[];
  };
  getAllArticleIds(): string[];
  getAllExternalCollectionIds(): string[];
  getDTO(
    deleteResolvedFields?: boolean,
    shouldDedupe?: boolean
  ): {
    root: Container;
    metadata: Metadata;
  };
  getRefDTO: {
    (withResolvedRoot: true): {
      type: DataModelTypeKeys;
      childRefs: string[];
      root: Container;
      metadata: Metadata;
    };
    (withResolvedRoot: false): {
      type: DataModelTypeKeys;
      childRefs: string[];
      metadata: Metadata;
    };
  };
  deleteResolvedFieldsFromExternalDTO<T extends Container>(dto: {
    root: T;
    metadata: Metadata;
  }): {
    root: T;
    metadata: Metadata;
  };
  fromAllessehContent(
    content: AllessehContent | AllessehSummariesIncludedAttributes,
    options?: FromAllessehContentOptions
  ): ArticleItem | LinkedItem;
  generateHierarchyId(entity: EntityComponent, parentHierarchyId?: string, index?: number): string;
  generateHierarchyIdFromDragEvent(droppableId: string, dropIndex: number): string;
  setNewRoot(newRoot: Container): void;
  applyDedupedRoot(dedupedRoot: Container): void;
  setNewMetadata(newMetadata: Partial<Metadata>): void;
  getEntity<E extends EntityComponent>(hierarchyId: string, options?: { indices?: number[]; root?: E }): E | null;
  swapEntities(srcHierarchyId: string, destHierarchyId: string): void;
  insertEntity(destHierarchyId: string, entity: EntityComponent | EntityComponent[]): string | boolean;
  modifyEntity(
    hierarchyId: string,
    callback: (entity: EntityComponent) => void,
    params?: { updateRoot?: boolean }
  ): void;
  clearModule(hierarchyId: string): void;
  modifyAllByType<E extends EntityComponent = EntityComponent>(type: string, callback: (entity: E) => void): void;
  applyOverfetchForModifiedQuery(queryId: string, query: Record<string, unknown>): void;
  applyOverfetchForAddedQuery(
    moduleHierarchyId: string,
    query: Record<string, unknown>,
    queryIdToExclude?: string
  ): PageModule | null;
  insertEntityToCollection(destHierarchyId: string, entity: ModuleContainerUnion): void;
  changeEntity(hierarchyId: string, callback: (oldEntity: EntityComponent) => EntityComponent): void;
  removeEntity(hierarchyId: string, cleanupDedupe?: boolean): EntityComponent | null;
  removeAllByType(type: string): void;
  cleanupDedupeForGroup(moduleHierarchyIds: string[]): void;
  getEmbargoContent(): ArticleItem[];
  getCancelledEmbargoContent(): ArticleItem[];
  getSortedEmbargoContent(sort: 'asc' | 'desc'): AllessehContent[];
  renderEntity(entity: EntityComponent, options: RenderEntityOptions): React.ReactElement;
  isEditingLinkedItem: Set<string>;
  setIsEditingLinkedItem: React.Dispatch<React.SetStateAction<Set<string>>>;
  isItemInDataModel(item: ContentItem): boolean;
  countContent: (entity: EntityComponent) => string;
  getAllModuleCounts: () => Record<string, number>;
  traverseEntity(
    entity: EntityComponent,
    callback: (entity: EntityComponent, hierarchyId: string) => void,
    options?: TraverseEntityOptions
  ): void;
  setIsQueryLoading: React.Dispatch<React.SetStateAction<boolean>>;
  queriesHaveSettled: boolean;
  getRootCopy: () => Container;
  setHasModelChangedOnce: React.Dispatch<React.SetStateAction<boolean>>;
  getArticlePromotionLogByOriginId: (originId?: string | null) => ArticlePromotionLogData | null;
  getSectionAttributes: () => SectionAttributes;
  setSectionAttributes: (newAttributes: SectionAttributes) => void;
  updateLastSavedState: () => void;
  getGroupConfig(groupId: string): unknown;
  getAllGroupConfigs(): Array<StructureGroupConfig & { id: string }>;
  isReadOnly: boolean;
  validations: PageValidation[];
}

const DEFAULT_STATE: IDataModelContext<Container> = {
  // @ts-expect-error - empty string is not assignable to type DataModelTypeKeys
  type: '',
  // @ts-expect-error - null is not assignable to type Container
  root: null,
  // @ts-expect-error - empty object is not assignable to type BaseMetadata
  metadata: {},
  hasModelChangedOnce: false,
  hasModelChangedRecently: false,
  setHasModelChangedRecently: () => {},
  resetModelChanged: () => {},
  readHierarchyId: () => [],
  getAllArticleIds: () => [],
  getAllExternalCollectionIds: () => [],
  // @ts-expect-error - null is not assignable to root and empty object is not assignable to metadata
  getDTO: () => ({ root: null, metadata: {} }),
  // @ts-expect-error - empty object is not assignable to metadata
  getRefDTO: () => ({ childRefs: [], metadata: {} }),
  // @ts-expect-error - empty object is not assignable to return type
  deleteResolvedFieldsFromExternalDTO: () => ({}),
  // @ts-expect-error - null is not assignable to type ArticleItem
  fromAllessehContent: () => null,
  generateHierarchyId: () => '',
  generateHierarchyIdFromDragEvent: () => '',
  setNewRoot: () => {},
  applyDedupedRoot: () => {},
  setNewMetadata: () => {},
  getEntity: () => null,
  swapEntities: () => {},
  insertEntity: () => true,
  modifyEntity: () => {},
  clearModule: () => {},
  applyOverfetchForModifiedQuery: () => {},
  applyOverfetchForAddedQuery: () => null,
  changeEntity: () => {},
  changeAllByType: () => {},
  removeEntity: () => null,
  removeAllByType: () => {},
  cleanupDedupeForGroup: () => {},
  getEmbargoContent: () => [],
  getCancelledEmbargoContent: () => [],
  dedupe: () => {},
  renderEntity: () => <></>,
  isItemInDataModel: () => false,
  traverseEntity: () => {},
  countContent: () => '',
  getAllModuleCounts: () => ({}),
  isEditingLinkedItem: new Set<string>(),
  setIsEditingLinkedItem: () => {},
  setIsQueryLoading: () => {},
  queriesHaveSettled: false,
  setHasModelChangedOnce: () => {},
  getArticlePromotionLogByOriginId: () => null,
  getSectionAttributes: () => ({}) as SectionAttributes,
  updateSectionAttributes: () => {},
  updateLastSavedState: () => {},
  getGroupConfig: () => undefined,
  getAllGroupConfigs: () => [],
  isReadOnly: false,
  validations: []
};

export const DataModelContext = createContext(DEFAULT_STATE);

export interface DataModelStableContextValue {
  generateHierarchyId: IDataModelContext<Container>['generateHierarchyId'];
  getArticlePromotionLogByOriginId: IDataModelContext<Container>['getArticlePromotionLogByOriginId'];
}

export const DataModelStableContext = createContext<DataModelStableContextValue>({
  generateHierarchyId: () => '',
  getArticlePromotionLogByOriginId: () => null
});
